/**
 * The frame every group screen sits in.
 *
 * The whole shell is one flex column the height of the viewport: header, then a single
 * scrolling area, then the mobile tab bar. The tab bar is a flex sibling rather than
 * `position: fixed`, so it cannot drift when a page inside scrolls, and no page needs
 * bottom padding to clear it.
 */

import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";

import { api, type Balances, type GroupSummary } from "../../lib/api";
import { cn } from "../../lib/utils";
import { PushBanner } from "../../features/auth/PushSettings";
import { useSession } from "../../features/auth/session";
import { BalanceStrip } from "./BalanceStrip";
import { GroupSwitcher } from "./GroupSwitcher";
import { ThemeToggle } from "./ThemeToggle";

const TABS = [
  { to: "", label: "Ledger", end: true },
  { to: "balances", label: "Balances" },
  { to: "stats", label: "Stats" },
  { to: "ai", label: "Assistant" },
  { to: "members", label: "Members" },
] as const;

const ADMIN_TAB = { to: "admin", label: "Admin" } as const;

export function useGroupId(): number {
  const { gid } = useParams();
  return Number(gid);
}

export function useGroup(groupId: number) {
  return useQuery({
    queryKey: ["group", groupId],
    queryFn: () => api.get<GroupSummary>(`/groups/${groupId}`),
    enabled: Number.isFinite(groupId),
  });
}

export function useBalances(groupId: number) {
  return useQuery({
    queryKey: ["balances", groupId],
    queryFn: () => api.get<Balances>(`/groups/${groupId}/balances`),
    enabled: Number.isFinite(groupId),
  });
}

export function GroupLayout() {
  const groupId = useGroupId();
  const navigate = useNavigate();
  const { user, logout } = useSession();
  const group = useGroup(groupId);
  const balances = useBalances(groupId);

  const currency = group.data?.base_currency ?? "USD";
  const tabs = [...TABS, ...(group.data?.my_role === "admin" ? [ADMIN_TAB] : [])];

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <header className="z-30 shrink-0 border-b border-rule bg-paper/90 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1400px] items-center gap-2 px-4 py-2.5 sm:gap-3 sm:px-6">
          {/*
            The way back out. On a phone the group switcher is the only thing in the header,
            and "how do I get to my other groups" was the first question everyone asked.
          */}
          <NavLink
            to="/"
            aria-label="All groups"
            title="All groups"
            className="display tap flex w-11 shrink-0 items-center justify-center rounded-lg border border-rule bg-raised text-[11px] tracking-[0.08em] text-muted transition-colors hover:border-ink/25 hover:text-ink"
          >
            TS
          </NavLink>
          <GroupSwitcher currentGroupId={groupId} />

          <nav className="ml-auto hidden items-center gap-0.5 md:flex" aria-label="Group sections">
            {tabs.map((tab) => (
              <NavLink
                key={tab.label}
                to={tab.to ? `/g/${groupId}/${tab.to}` : `/g/${groupId}`}
                end={"end" in tab ? tab.end : false}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-ink/[0.06] font-medium text-ink"
                      : "text-muted hover:bg-ink/[0.03] hover:text-ink",
                  )
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-0.5 md:ml-3">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => void logout().then(() => navigate("/login"))}
              className="whitespace-nowrap rounded-lg px-2.5 py-2 text-xs text-muted transition-colors hover:bg-ink/[0.03] hover:text-ink"
              title={user?.email}
            >
              Sign out
            </button>
          </div>
        </div>

        {balances.data ? (
          <div className="mx-auto w-full max-w-[1400px] px-4 pb-2.5 sm:px-6">
            <BalanceStrip
              balances={balances.data.balances}
              currency={currency}
              myMemberId={group.data?.my_member_id}
              compact
            />
          </div>
        ) : null}
      </header>

      {/* Asks once per device, under the header on whichever page signing in lands on. */}
      <div className="mx-auto w-full max-w-[1400px] shrink-0 px-4 sm:px-6">
        <PushBanner />
      </div>

      {/*
        `overflow-x: clip`, not `hidden`. A `hidden` box is still a scroll container, and
        iOS Safari will happily scroll one — even with no scrollbar — to bring a focused
        input into view, which is how the entry form ended up shifted sideways with its
        left padding hidden. `clip` creates no scrollport at all, so there is nothing to
        scroll. Anything genuinely wide (the sheet-mode table) scrolls in its own box.
      */}
      <main className="mx-auto flex w-full min-h-0 max-w-[1400px] flex-1 flex-col overflow-x-clip overflow-y-hidden px-4 sm:px-6">
        <Outlet />
      </main>

      <nav
        className="z-30 shrink-0 border-t border-rule bg-paper md:hidden"
        aria-label="Sections"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5 items-center">
          <TabLink to={`/g/${groupId}`} label="Ledger" end />
          <TabLink to={`/g/${groupId}/balances`} label="Balances" />
          <div className="flex justify-center py-1.5">
            <button
              type="button"
              onClick={() => navigate(`/g/${groupId}/new`)}
              aria-label="Add entry"
              className="flex h-11 w-14 items-center justify-center rounded-xl bg-accent text-2xl leading-none text-accent-ink shadow-sm transition-transform active:scale-95"
            >
              +
            </button>
          </div>
          <TabLink to={`/g/${groupId}/ai`} label="Assistant" />
          <TabLink to={`/g/${groupId}/members`} label="More" />
        </div>
      </nav>
    </div>
  );
}

function TabLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex min-h-[52px] flex-col items-center justify-center gap-0.5 text-2xs transition-colors",
          isActive ? "font-medium text-accent" : "text-muted",
        )
      }
    >
      {label}
    </NavLink>
  );
}
